import axios from 'axios';

// 统一使用完整URL，不再区分开发和生产环境
const API_URL = process.env.REACT_APP_BASE_URL || 'https://vbauio.xyz';
// const API_URL = 'http://127.0.0.1:5000'

// 创建不带认证的axios实例
const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30秒超时
});

// 创建带认证的axios实例
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30秒超时
});

// Token 过期事件处理器
let tokenExpirationHandler = null;

// 设置 Token 过期处理器
export const setTokenExpirationHandler = (handler) => {
  tokenExpirationHandler = handler;
};

// 添加请求拦截器用于认证
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 添加响应拦截器处理错误
const handleResponseError = (error) => {
  if (error.response) {
    // 服务器返回错误状态码
    console.error('API错误:', error.response.status, error.response.data);
    
    // 处理401未授权错误（Token无效或过期）
    if (error.response.status === 401) {
      const isTokenExpired = error.response.data?.expired === true;
      
      // 清除本地token
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      
      // 如果是token过期，触发过期处理
      if (isTokenExpired && tokenExpirationHandler) {
        tokenExpirationHandler();
      }
      
      // 如果不是在登录页面，重定向到登录页
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?expired=true';
      }
    }
  } else if (error.request) {
    // 请求已发送但没有收到响应
    console.error('网络错误:', error.request);
  } else {
    // 请求设置时出错
    console.error('请求错误:', error.message);
  }
  
  return Promise.reject(error);
};

api.interceptors.response.use(response => response, handleResponseError);

// API 函数
export const login = async (username, password) => {
  try {
    const response = await publicApi.post('/api/auth/login', { username, password });
    // 验证响应数据结构
    const { token, isAdmin, _id } = response.data;
    
    if (!token || !isAdmin || !_id) {
      throw new Error('无效的登录响应数据');
    }
    return response;
  } catch (error) {
    if (error.response?.data?.message) {
      throw error;
    }
    throw new Error('登录失败，请稍后重试');
  }
};

export const logout = async () => {
  const response = await api.post('/api/auth/logout');
  return response;
};

export const getCurrentUser = async () => {
  const response = await api.get('/api/auth/current-user');
  return response;
};

// License API
export const getLicenses = (page = 1, limit = 10, search = '') => {
  // 使用新的search参数支持多字段搜索
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  return api.get(`/api/licenses?page=${page}&limit=${limit}${searchParam}`);
};

// 获取营业执照 - 两个版本：带认证和不带认证
export const getLicense = (id) => {
  if (!id) {
    console.error('getLicense: ID参数为空');
    return Promise.reject(new Error('ID参数不能为空'));
  }
  
  // 记录原始ID
  console.log('getLicense: 原始ID:', id);
  
  try {
    // 如果可能，对ID进行解码以查看原始内容
    // 这有助于调试
    let decodedForDebug = '';
    try {
      if (id.includes('%')) {
        decodedForDebug = decodeURIComponent(id);
        console.log('getLicense: 解码后ID (仅用于调试):', decodedForDebug);
      }
    } catch (e) {
      console.warn('getLicense: ID解码失败 (仅调试用):', e);
    }
    
    // 检查是否需要编码（如果已经包含%字符，说明已经被编码）
    const isAlreadyEncoded = id.includes('%');
    const encodedId = isAlreadyEncoded ? id : encodeURIComponent(id);
    
    console.log('getLicense: 最终使用的编码ID:', encodedId);
    
    // 根据ID特征判断是否为公开URL访问链接（通常包含=号）
    // = 在URL中编码为 %3D
    const isPublicAccess = id.includes('=') || id.includes('%3D') || (decodedForDebug && decodedForDebug.includes('='));
    
    if (isPublicAccess) {
      // console.log('getLicense: 使用公开API');
      return publicApi.get(`/api/license/${encodedId}`);
    } else {
      // console.log('getLicense: 使用认证API');
      return api.get(`/api/license/${encodedId}`);
    }
  } catch (error) {
    console.error('getLicense: 处理ID时出错:', error);
    return Promise.reject(error);
  }
};

export const createLicense = (licenseData) => {
  return api.post('/api/license', licenseData);
};

export const updateLicense = (id, licenseData) => {
  return api.put(`/api/license/${id}`, licenseData);
};

export const deleteLicense = (id) => {
  // console.log("接收到的执照id: ",id)
  return api.delete(`/api/license/${id}`);
};

// Toggle license status (enable/disable)
export const toggleLicenseStatus = (id) => {
  return api.put(`/api/license/${id}/toggle-status`);
};

// Batch disable licenses by date
export const batchDisableLicenses = (datePattern) => {
  return api.put('/api/licenses/batch-disable', { datePattern });
};

// Temporary enable license for specified duration
export const temporaryEnableLicense = (id, duration, unit) => {
  return api.put(`/api/license/${id}/temporary-enable`, { duration, unit });
};

// Check and disable expired temporary licenses
export const checkExpiredLicenses = () => {
  return api.put('/api/licenses/check-expired');
};

// Get license status info
export const getLicenseStatus = (id) => {
  return api.get(`/api/license/${id}/status`);
};

// Update license versions
export const updateLicenseVersions = async () => {
  return api.post('/api/licenses/update-versions');
};

// Check expired licenses and update versions
export const checkExpiredAndUpdateVersions = async () => {
  return api.post('/api/licenses/check-expired-and-update');
};

// Batch temporary enable licenses (自动获取所有未禁用的营业执照，统一设置一个月的临时启用期)
export const batchTemporaryEnableLicenses = async () => {
  return api.put('/api/licenses/batch-temporary-enable');
};

// 导出 API 实例供其他模块使用
export { api, publicApi }; 